import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import debounce from 'lodash/debounce'

import Grid from '@mui/material/Grid'

import { get } from 'utils/lodash'
import { getAddressParams } from 'utils/helper'
import { HHFormTextField, HHFormAutocompleteField } from 'components/form-fields/v5'
import { useLazyGetAddressSuggestListQuery } from 'api/map/getAddressSuggestList'

import T from 'T'
import { DEFAULT_ADDRESS_FIELD_KEY, DEFAULT_STATE } from './model'

const { line1Placeholder, line2Placeholder, parcelNumberPlaceholder } = DEFAULT_STATE

const AddressRowOne = ({
  fieldPrefix = DEFAULT_ADDRESS_FIELD_KEY,
  line1 = '',
  line1Label = T.STREET,
  line1Required = false,
  showLine2 = true,
  autocompleteSlotProps = {},
  control,
  showParcelNumber = false,
  hasError = {},
  onLine1Change,
  onBillingAddressChange,
  onKeyDown = noop,
}) => {
  const [getSuggestList, { data }] = useLazyGetAddressSuggestListQuery()
  const addressList = get(data, 'suggestions', [])

  const handleAddressLineChange = useMemo(() => debounce(value => getSuggestList({ search: value }), 300), [])

  useEffect(() => {
    return () => {
      handleAddressLineChange.cancel()
    }
  }, [])

  return (
    <>
      <Grid size={12}>
        <HHFormAutocompleteField
          clearOnBlur={false}
          blurOnSelect
          label={line1Required ? `${line1Label}*` : line1Label}
          required={line1Required}
          slotProps={autocompleteSlotProps}
          selectOnFocus={false}
          openOnFocus
          noOptionsText={T.SEARCH_ADDRESS_PLACEHOLDER}
          control={control}
          name={`${fieldPrefix}.line1`}
          placeholder={line1Placeholder}
          deprecatedLabel={false}
          error={Boolean(hasError.line1)}
          helperText={hasError.line1?.message}
          getOptionLabel={option => {
            if (option?.text) {
              const { line1: addressLine1, city, state, zipCode } = getAddressParams(option)
              return (
                <>
                  {addressLine1}
                  <br />
                  {city ? `${city}, ` : ''}
                  {`${state} ${zipCode}`.trim()}
                </>
              )
            }
            return line1
          }}
          filterOptions={x => x}
          options={addressList}
          includeInputInList
          filterSelectedOptions
          onInputChange={(event, newInputValue) => {
            onLine1Change(newInputValue)
            if (event?.type !== 'change') {
              return
            }

            if (newInputValue) {
              handleAddressLineChange(newInputValue)
            }
          }}
          onChange={(event, address) => {
            if (!address) {
              return
            }

            onBillingAddressChange(address)
          }}
        />
      </Grid>
      {(showLine2 || showParcelNumber) && (
        <Grid container spacing={2} sx={{ width: '100%' }}>
          {showLine2 && (
            <Grid size="grow">
              <HHFormTextField
                fullWidth
                control={control}
                deprecatedLabel={false}
                label={T.STREET_LINE_2}
                name={`${fieldPrefix}.unitNumber`}
                placeholder={line2Placeholder}
                onKeyDown={onKeyDown}
              />
            </Grid>
          )}
          {showParcelNumber && (
            <Grid
              size={{
                xs: 12,
                md: 3,
              }}
            >
              <HHFormTextField
                fullWidth
                control={control}
                deprecatedLabel={false}
                label={T.PARCEL_NUMBER}
                name="parcelNumber"
                placeholder={parcelNumberPlaceholder}
                onKeyDown={onKeyDown}
              />
            </Grid>
          )}
        </Grid>
      )}
    </>
  )
}

AddressRowOne.propTypes = {
  fieldPrefix: PropTypes.string,
  line1Label: PropTypes.string,
  line1Required: PropTypes.bool,
  showLine2: PropTypes.bool,
  autocompleteSlotProps: PropTypes.object,
  line1: PropTypes.string,
  control: PropTypes.object.isRequired,
  showParcelNumber: PropTypes.bool,
  hasError: PropTypes.shape({
    line1: PropTypes.any,
  }),
  onLine1Change: PropTypes.func.isRequired,
  onBillingAddressChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
}

export default AddressRowOne
